import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import 'react-toastify/dist/ReactToastify.css';

import { getDatabase, ref, set, onDisconnect } from 'firebase/database';
import { subscribeUserToPush } from './push-notification';

import { ToastContainer } from 'react-toastify';
import { AuthProvider, useAuth } from './context/AuthContext';

import Navbar from './components/Navbar';
import Footer from './components/Footer';
import SocialSidebar from './components/SocialSidebar';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import InstallBanner from './components/InstallPrompt/InstallBanner';

// Public Components
import Home from './components/Home';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import About from './components/About';
import Contact from './components/Contact';
import Disclaimer from './components/Disclaimer';
import JobForm from './components/JobForm';
import JobSuccess from './components/JobSuccess';
import SubscribeForm from './components/SubscribeForm';
import UnsubscribeForm from './components/UnsubscribeForm';
import PasswordChangedSuccess from './components/PasswordChangedSuccess';
import JobCount from './components/JobCount';
import Vision from './components/Vision';
import JobDetails from "./JobDetails";
// Private Components
import Profile from './components/Profile';
import ChangePassword from './components/ChangePassword';
import Jobs from './Jobs';
import OnlineUsers from './components/OnlineUsersPage';
import AllUsersPage from './pages/admin/email/AllUsersPage';
// Admin Components
import AdminDashboard from './components/AdminDashboard';
import SendNotification from './pages/SendNotification';
import SendAlerts from './pages/admin/SendAlerts';
import EmailDashboard from './pages/admin/EmailDashboard';
import EmailLogs from './pages/admin/email/EmailLogs';
import EmailSummary from './pages/admin/email/EmailSummary';
import SendEmailPage from './pages/admin/email/SendEmailPage';
import UnsubscribedList from './pages/admin/email/UnsubscribedList';
import DailyEmailStats from './pages/admin/email/DailyEmailStats';

// Fallback
import NotFound from './pages/NotFound';

import './styles/global.css';

const AppContent = () => {
  const { currentUser: user, loading } = useAuth();

  useEffect(() => {
    subscribeUserToPush();
  }, []);

  useEffect(() => {
    if (user) {
      const db = getDatabase();
      const userRef = ref(db, `onlineUsers/${user.uid}`);
      set(userRef, {
        email: user.email,
        uid: user.uid,
        lastActive: new Date().toISOString(),
      });
      onDisconnect(userRef).remove();
    }
  }, [user]);

  if (loading) return <div className="text-center py-5">🔄 Loading...</div>;

  return (
    <Router>
      <Navbar />
      <SocialSidebar />

      <div style={{ padding: '1rem', minHeight: '80vh' }}>
        <Routes>
          {/* Public */}
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/job-success" element={<JobSuccess />} />
          <Route path="/post-job" element={<JobForm />} />
        <Route path="/password-changed-success" element={<PasswordChangedSuccess />} />


<Route
  path="/subscribe"
  element={
    <PrivateRoute>
      <SubscribeForm />
    </PrivateRoute>
  }
/>

<Route
  path="/unsubscribe"
  element={
    <PrivateRoute>
      <UnsubscribeForm />
    </PrivateRoute>
  }
/>

          {/* Protected */}
          <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
          <Route path="/change-password" element={<PrivateRoute><ChangePassword /></PrivateRoute>} />
          <Route path="/submit-job" element={<PrivateRoute><JobForm /></PrivateRoute>} />
          <Route path="/jobs" element={<PrivateRoute><Jobs /></PrivateRoute>} />
<Route path="/jobs/:id" element={<PrivateRoute><JobDetails /></PrivateRoute>} />
          {/* Admin */}
          <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/admin/job-form" element={<AdminRoute><JobForm /></AdminRoute>} />
          <Route path="/admin/job-form/:id" element={<AdminRoute><JobForm /></AdminRoute>} />
          <Route path="/admin/online-users" element={<AdminRoute><OnlineUsers /></AdminRoute>} />
          <Route path="/admin/send-alerts" element={<AdminRoute><SendAlerts /></AdminRoute>} />
          <Route path="/admin/send-notification" element={<AdminRoute><SendNotification /></AdminRoute>} />
<Route path="/admin/job-stats" element={<AdminRoute><JobCount /></AdminRoute>} />
          {/* Email Dashboard Entry */}
          <Route path="/admin/email-dashboard" element={<AdminRoute><EmailDashboard /></AdminRoute>} />
<Route path="/admin/email/all-users" element={<AdminRoute><AllUsersPage /></AdminRoute>} />

          {/* Split Email Pages */}
          <Route path="/admin/email/summary" element={<AdminRoute><EmailSummary /></AdminRoute>} />
          <Route path="/admin/email/send" element={<AdminRoute><SendEmailPage /></AdminRoute>} />
          <Route path="/admin/email/logs" element={<AdminRoute><EmailLogs /></AdminRoute>} />
          <Route path="/admin/email/unsubscribed" element={<AdminRoute><UnsubscribedList /></AdminRoute>} />
          <Route path="/admin/email/stats-daily" element={<AdminRoute><DailyEmailStats /></AdminRoute>} />
<Route path="/vision" element={<Vision />} />

          {/* Fallback */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>

      <Footer />
      <InstallBanner />

      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        pauseOnFocusLoss={false}
        draggable={false}
        pauseOnHover={false}
        theme="colored"
      />
    </Router>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
